import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Button,
  IconButton,
  Stack,
  Tooltip,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { getVideoTutorials } from "../../api/videoTutorial.service";
import ViewVideoDialog from "./components/ViewVideoDialog";
import VideoTutorialFormDialog from "./components/VideoTutorialFormDialog";

const VIDEO_TUTORIAL_TYPE_LABELS = {
  onboard: "Onboarding",
  feature: "Features",
  metodology: "Methodology",
  testimonials: "Testimonials",
  grammar: "Grammar",
  aboutUs: "About us",
};

const VideoTutorials = () => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewOpen, setViewOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editItem, setEditItem] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getVideoTutorials();
      const results = response?.data?.results ?? [];
      setData(results);
    } catch (error) {
      console.error("Error fetching video tutorials:", error);
      toast.error(t("toast.errorLoading") || "Error loading video tutorials");
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleView = (item) => {
    setSelectedItem(item);
    setViewOpen(true);
  };

  const handleEdit = (item) => {
    setEditId(item?._id);
    setEditItem(item);
    setFormOpen(true);
  };

  const handleCreate = () => {
    setEditId(null);
    setEditItem(null);
    setFormOpen(true);
  };

  const handleFormSuccess = () => {
    fetchData();
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <ViewVideoDialog open={viewOpen} onClose={() => setViewOpen(false)} item={selectedItem} />
      <VideoTutorialFormDialog
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditId(null); setEditItem(null); }}
        editId={editId}
        editItem={editItem}
        onSuccess={handleFormSuccess}
      />

      <Paper sx={{ borderRadius: 1, overflow: "hidden", display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
        <Box sx={{ p: 3, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography variant="h6" fontWeight={700}>
              {t("pages.videotutorials")}
            </Typography>
            <Chip label={data.length} size="small" sx={{ fontWeight: 700, fontSize: 13 }} />
          </Stack>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
            {t("videotutorials.createNew")}
          </Button>
        </Box>

        <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 12, textTransform: "uppercase" }}>
                  {t("common.title")}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 12, textTransform: "uppercase" }}>
                  {t("videotutorials.category")}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 12, textTransform: "uppercase" }}>
                  {t("common.status")}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 12, textTransform: "uppercase" }} align="right">
                  {t("common.actions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4, color: "text.secondary" }}>
                    {t("videotutorials.empty") || "No video tutorials found"}
                  </TableCell>
                </TableRow>
              ) : (
                data.map((item, index) => (
                  <TableRow key={item?._id || index} hover sx={{ "&:last-child td": { border: 0 } }}>
                    <TableCell>{item?.trans || "—"}</TableCell>
                    <TableCell>
                      <Chip
                        label={VIDEO_TUTORIAL_TYPE_LABELS[item?.videoTutorialType] || item?.videoTutorialType || "—"}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item?.active ? t("common.active") : t("common.inactive")}
                        size="small"
                        color={item?.active ? "success" : "default"}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title={t("videotutorials.viewVideo")}>
                          <IconButton size="small" onClick={() => handleView(item)}>
                            <PlayArrowIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("common.edit")}>
                          <IconButton size="small" onClick={() => handleEdit(item)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default VideoTutorials;
